'use client';

import { Suspense, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import NProgress from 'nprogress';

import { usePathname } from '@/navigation';
import { progressTracker } from '@/utils/progress-tracker';

import StyledProgressBar from './styles';

/** Delay to let React Query hooks on the new page start fetching before decrementing (ms) */
const ROUTE_CHANGE_SETTLE_DELAY_MS = 150;

/** Duration for the progress bar to animate to 100% (ms) */
const PROGRESS_DONE_ANIMATION_MS = 300;

function NProgressDone() {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  useEffect(() => {
    // Delay finish to let React Query hooks on the new page
    // start fetching before navigation is marked complete.
    // This prevents the bar from hiding between route change and API request start.
    const timer = setTimeout(() => {
      progressTracker.finishNavigation();
    }, ROUTE_CHANGE_SETTLE_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react/exhaustive-effect-dependencies -- a route change is the trigger, not an input
  }, [pathname, searchParams]);

  return null;
}

// ---------------------------------------------------------------------- //

type HistoryStateInput = [data: unknown, unused: string, url?: string | URL | null | undefined];

/** Only start navigation when the visible URL actually changes (ignore internal state-only updates) */
const hasUrlChanged = (url?: string | URL | null) => {
  if (!url) {
    return false;
  }

  const { pathname, search } = new URL(String(url), globalThis.location.origin);

  return pathname + search !== globalThis.location.pathname + globalThis.location.search;
};

const createHistoryProxy = (original: typeof history.pushState) =>
  new Proxy(original, {
    apply: (target, thisArg, argArray: HistoryStateInput) => {
      if (hasUrlChanged(argArray[2])) {
        progressTracker.startNavigation();
      }
      return target.apply(thisArg, argArray);
    },
  });

export default function ProgressBar() {
  useEffect(() => {
    NProgress.configure({ showSpinner: false, speed: PROGRESS_DONE_ANIMATION_MS });

    const originalPushState = globalThis.history.pushState;
    const originalReplaceState = globalThis.history.replaceState;

    globalThis.history.pushState = createHistoryProxy(originalPushState);
    globalThis.history.replaceState = createHistoryProxy(originalReplaceState);

    return () => {
      globalThis.history.pushState = originalPushState;
      globalThis.history.replaceState = originalReplaceState;
    };
  }, []);

  return (
    <>
      <StyledProgressBar />
      <Suspense fallback={null}>
        <NProgressDone />
      </Suspense>
    </>
  );
}
